import * as React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge, BadgeVariants } from "~/components/Badge";
import {
    ColumnMetadata,
    ColumnTypes,
    DataGrid,
    RowSizes,
} from "~/components/DataGrid";
import { DataGridPercentValue } from "~/components/DataGrid/DataGridPercentValue";
import { ImageLoader } from "~/components/ImageLoader";
import { Link } from "~/components/Link";
import { Emdash } from "~/components/Punctuation";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { MultiSelect, MultiSelectOption } from "~/components/MultiSelect";
import { RWA_COIN_ICON } from "~/consts";
import { AssetTableRow } from "~/modules/assets/components/AssetTable/AssetTableRow";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { FilterLabel } from "~/modules/asset_screener/pages/listing/FilterLabel";
import { ASSET_CLASS_COLUMN } from "~/modules/assets/components/AssetTable/columns/AssetClass";
import { INCEPTION_DATE_COLUMN } from "~/modules/assets/components/AssetTable/columns/InceptionDate";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN } from "~/modules/assets/components/AssetTable/columns/PrimaryMarket";
import { ASSET_SORT_MAP } from "~/modules/assets/components/AssetTable/useInMemoryAssetSort";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    ApiQuery,
    ApiQueryEndpoints,
    FilterMetadata,
    PaginationState,
    QueryResponse,
    SelectOption,
    SortDirections,
} from "~/modules/filters/types";
import {
    ApiQueryFetcher,
    ApiQueryStateManager,
} from "~/modules/filters/components/ApiSearchLayout";
import {
    FilterFormContextProvider,
    FilterFormField,
} from "~/modules/filters/components/FilterFormContext";
import { areFiltersIdentical } from "~/modules/filters/areFiltersIdentical";
import { SEARCH_FILTER_METADATA } from "~/modules/filters/consts";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import * as Analytics from "~/modules/new_asset_monitor/analytics";
import { NEW_ASSET_MONITOR_QUERY } from "~/modules/new_asset_monitor/consts";
import {
    ChartTypes,
    TimeSeriesUnits,
    TimeSeriesView,
} from "~/modules/token_timeseries/types";
import { cn } from "~/lib/utils";
import { getAssetClassLinkMetadata } from "~/utils/getAssetClassLinkMetadata";
import { getMeasureValue } from "~/utils/getMeasureValue";
import { getAssetURL } from "~/utils/getAssetURL";
import { formatCurrencyValue } from "~/utils/functions/formatCurrencyValue";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { RWA_COLORS } from "~/styles/colors";
import { SelectLoginGate } from "~/components/SelectInput/SelectLoginGate";
import { useUser } from "~/modules/user/hooks/useUser";

// // // //

type IssueCountGroupingValue =
    "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
const DEFAULT_TABLE_GROUPING: IssueCountGroupingValue = "weekly";
const DEFAULT_CHART_GROUPING: IssueCountGroupingValue = "monthly";
const UNKNOWN_REGISTRATION_GROUP_KEY = "__unknown_registration_date__";

// Debounces the search input before it's applied to the ApiQuery (matches SearchValueInput)
const SEARCH_DEBOUNCE_TIMEOUT = 800;
const FALLBACK_ASSET_CLASS_CHART_COLORS = [
    RWA_COLORS.blue[500],
    RWA_COLORS.emerald[500],
    RWA_COLORS.amber[500],
    RWA_COLORS.indigo[500],
    RWA_COLORS.teal[500],
    RWA_COLORS.orange[500],
    RWA_COLORS.cyan[500],
    RWA_COLORS.green[500],
    RWA_COLORS.slate[500],
];

const GROUP_DAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
});

const GROUP_WEEK_FORMATTER = GROUP_DAY_FORMATTER;

const GROUP_MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
});

const ISSUE_COUNT_GROUPING_OPTIONS: Array<{
    value: IssueCountGroupingValue;
    label: string;
}> = [
    {
        value: "daily",
        label: "Day",
    },
    {
        value: "weekly",
        label: "Week",
    },
    {
        value: "monthly",
        label: "Month",
    },
    {
        value: "quarterly",
        label: "Quarter",
    },
    {
        value: "yearly",
        label: "Year",
    },
];

const CONTROL_INPUT_CLASS_NAME =
    "h-9 rounded-md bg-card px-3 py-1.5 text-sm shadow-xs md:text-sm disabled:border-slate-300 disabled:bg-slate-100 disabled:text-slate-500 disabled:placeholder:text-slate-500 disabled:opacity-100";

const CONTROL_SELECT_TRIGGER_CLASS_NAME =
    "w-full min-w-0 h-9 rounded-md border border-input bg-card px-3 py-1.5 text-sm text-foreground shadow-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-100 disabled:text-slate-500 disabled:opacity-100 disabled:hover:bg-slate-100 select-none";

const ISSUANCE_DATE_COLUMN_WIDTH = 140;
const REGISTRATION_DATE_COLUMN_WIDTH = 130;
const ASSET_COLUMN_WIDTH = 350;

const REGISTRATION_DATE_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns._created_at,
    label: (
        <span className="flex flex-col items-end text-right leading-tight whitespace-normal">
            <span>Registration</span>
            <span>Date</span>
        </span>
    ),
    type: ColumnTypes.date,
    width: REGISTRATION_DATE_COLUMN_WIDTH,
    sortsOn: AssetColumns._created_at,
    loginRequired: true,
    cellValue: (item) => {
        if (item.isAsset) {
            return item._created_at ?? null;
        }

        return null;
    },
};

const ISSUANCE_DATE_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    ...INCEPTION_DATE_COLUMN,
    label: "Issuance Date",
    width: ISSUANCE_DATE_COLUMN_WIDTH,
    sortsOn: AssetColumns.inceptionDate,
    loginRequired: true,
};

const ASSET_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    slug: AssetColumns.name,
    label: "Asset Name",
    type: ColumnTypes.string,
    width: ASSET_COLUMN_WIDTH,
    sortsOn: AssetColumns.name,
    loginRequired: true,
    render: (item) => {
        if (!item.isAsset) {
            return <Emdash />;
        }

        return (
            <Link
                href={getAssetURL({ asset: item })}
                variant="default"
                className="select-none pl-3 flex items-center gap-2"
                analyticsLabel={AssetUserEvents.ClickIndividualLink}
            >
                <ImageLoader
                    src={item.icon_url ?? RWA_COIN_ICON}
                    alt={`${item.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="shrink-0 rounded-full h-5 w-5 min-w-5"
                />
                <span className="dg-overflow">{item.name}</span>
            </Link>
        );
    },
};

const MONITOR_ASSET_CLASS_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    ...ASSET_CLASS_COLUMN,
    // proPlanRequired: true,
    width: 170,
    sortsOn: undefined,
};

const MONITOR_PLATFORM_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> =
    {
        ...PLATFORM_COLUMN,
        loginRequired: true,
        width: 180,
        sortsOn: AssetColumns.protocolNames,
    };

const CURRENT_ASSET_VALUE_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.totalAssetValueDollar,
    label: (
        <span className="flex flex-col items-end text-right leading-tight whitespace-normal">
            <span>Net Flows</span>
            <span className="text-xs">(since launch)</span>
        </span>
    ),
    type: ColumnTypes.dollar,
    width: 180,
    // proPlanRequired: true,
    sortsOn: undefined,
    render: (item) => {
        if (!item.isAsset) {
            return <Emdash />;
        }

        const value = getMeasureValue(item, "total_asset_value_dollar")?.val;

        if (typeof value !== "number") {
            return <Emdash />;
        }

        return (
            <span className="font-medium tabular-nums text-emerald-600">
                +${formatCurrencyValue(Math.abs(value))}
            </span>
        );
    },
};

const MIN_INVESTMENT_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    ...PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
    width: 165,
    loginRequired: true,
};

const ELIGIBLE_INVESTORS_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketInvestorTypes,
    label: "Eligible Investors",
    sortsOn: AssetColumns.primaryMarketInvestorTypes,
    type: ColumnTypes.string,
    width: 170,
    loginRequired: true,
    render: (item) => {
        if (!item.isAsset) {
            return <Emdash />;
        }

        const eligibleInvestorTypes = getEligibleInvestorTypes(item);

        if (eligibleInvestorTypes.length === 0) {
            return <Emdash />;
        }

        return (
            <div className="w-full overflow-hidden">
                <div className="flex flex-nowrap items-center gap-1 overflow-hidden">
                    {eligibleInvestorTypes.map((value) => (
                        <Badge
                            key={value}
                            size="sm"
                            rounded="sm"
                            className="shrink-0 whitespace-nowrap cursor-default"
                            label={value}
                            variant={BadgeVariants.gray}
                        />
                    ))}
                </div>
            </div>
        );
    },
};

const INDICATIVE_YIELD_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> =
    {
        slug: "indicative_yield" as AssetColumns,
        label: "Indicative Yield",
        tooltip: "Uses yield to maturity when available, otherwise 30D APY.",
        type: ColumnTypes.percent,
        width: 135,
        loginRequired: true,
        render: (item) => {
            if (!item.isAsset) {
                return <Emdash />;
            }

            const indicativeYield = getIndicativeYield(item);

            if (indicativeYield === null) {
                return <Emdash />;
            }

            return <DataGridPercentValue value={indicativeYield.value} />;
        },
    };

const TABLE_COLUMNS: ColumnMetadata<AssetTableRowData, AssetColumns>[] = [
    ASSET_COLUMN,
    MONITOR_ASSET_CLASS_COLUMN,
    REGISTRATION_DATE_COLUMN,
    CURRENT_ASSET_VALUE_COLUMN,
    MONITOR_PLATFORM_COLUMN,
    MIN_INVESTMENT_COLUMN,
    ELIGIBLE_INVESTORS_COLUMN,
    INDICATIVE_YIELD_COLUMN,
    ISSUANCE_DATE_COLUMN,
];

const TABLE_MIN_WIDTH = TABLE_COLUMNS.reduce(
    (totalWidth, column) => totalWidth + column.width,
    0,
);

// // // //

function normalizeDate(value: string | Date | null | undefined) {
    if (!value) {
        return null;
    }

    const dateValue = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
        return null;
    }

    return dateValue;
}

function getIndicativeYield(asset: Asset) {
    const yieldToMaturity = asset.yield_to_maturity_percent?.val;

    if (typeof yieldToMaturity === "number") {
        return {
            value: yieldToMaturity,
            label: "YTM",
        };
    }

    const apy30Day = getMeasureValue(asset, "apy_30_day");

    if (apy30Day && typeof apy30Day.val === "number") {
        return {
            value: apy30Day.val / 100,
            label: "30D APY",
        };
    }

    return null;
}

function getEligibleInvestorTypes(asset: Asset) {
    if (!asset.primary_markets || asset.primary_markets.length === 0) {
        return [];
    }

    return uniqueValues(
        asset.primary_markets.reduce<string[]>((acc, market) => {
            const values = market.allowed_investor_types || [];
            return [...acc, ...values];
        }, []),
    ).filter((value) => value.trim() !== "");
}

function getUtcStartOfDay(date: Date) {
    return new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
    );
}

function getUtcStartOfWeek(date: Date) {
    const startOfDay = getUtcStartOfDay(date);
    const daysSinceMonday = (startOfDay.getUTCDay() + 6) % 7;

    startOfDay.setUTCDate(startOfDay.getUTCDate() - daysSinceMonday);

    return startOfDay;
}

function getUtcStartOfMonth(date: Date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function getUtcStartOfQuarter(date: Date) {
    return new Date(
        Date.UTC(
            date.getUTCFullYear(),
            Math.floor(date.getUTCMonth() / 3) * 3,
            1,
        ),
    );
}

function getUtcStartOfYear(date: Date) {
    return new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
}

function formatRegistrationGroupLabel(
    date: Date,
    grouping: IssueCountGroupingValue,
) {
    if (grouping === "daily") {
        return GROUP_DAY_FORMATTER.format(date);
    }

    if (grouping === "weekly") {
        return `Week of ${GROUP_WEEK_FORMATTER.format(date)}`;
    }

    if (grouping === "monthly") {
        return GROUP_MONTH_FORMATTER.format(date);
    }

    if (grouping === "quarterly") {
        return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`;
    }

    if (grouping === "yearly") {
        return `${date.getUTCFullYear()}`;
    }

    return GROUP_MONTH_FORMATTER.format(date);
}

function getRegistrationGroup(asset: Asset, grouping: IssueCountGroupingValue) {
    const registrationDate = normalizeDate(asset._created_at);

    if (!registrationDate) {
        return {
            key: `${UNKNOWN_REGISTRATION_GROUP_KEY}-${grouping}`,
            label: "Registration Date Unavailable",
            timestamp: null,
        };
    }

    const groupStartDate =
        grouping === "daily"
            ? getUtcStartOfDay(registrationDate)
            : grouping === "weekly"
              ? getUtcStartOfWeek(registrationDate)
              : grouping === "monthly"
                ? getUtcStartOfMonth(registrationDate)
                : grouping === "quarterly"
                  ? getUtcStartOfQuarter(registrationDate)
                  : getUtcStartOfYear(registrationDate);

    return {
        key: `${grouping}-${groupStartDate.getUTCFullYear()}-${String(
            groupStartDate.getUTCMonth() + 1,
        ).padStart(2, "0")}-${String(groupStartDate.getUTCDate()).padStart(
            2,
            "0",
        )}`,
        label: formatRegistrationGroupLabel(groupStartDate, grouping),
        timestamp: groupStartDate.getTime(),
    };
}

function buildIssueCountSeries(
    assets: Asset[],
    grouping: IssueCountGroupingValue,
): TimeSeriesView[] {
    const seriesByAssetClass = new Map<
        string,
        {
            id: number;
            name: string;
            color: string;
            counts: Map<number, number>;
        }
    >();

    assets.forEach((asset) => {
        const registrationGroup = getRegistrationGroup(asset, grouping);

        if (registrationGroup.timestamp === null) {
            return;
        }

        const assetClassKey =
            asset.asset_class?.slug ??
            asset.asset_class_name ??
            `unknown-${asset.asset_class_id ?? 0}`;
        const assetClassName =
            asset.asset_class?.name ??
            asset.asset_class_name ??
            "Unknown Asset Class";

        const existingSeries = seriesByAssetClass.get(assetClassKey);

        if (existingSeries) {
            existingSeries.counts.set(
                registrationGroup.timestamp,
                (existingSeries.counts.get(registrationGroup.timestamp) ?? 0) +
                    1,
            );
            return;
        }

        const nextSeriesIndex = seriesByAssetClass.size;

        seriesByAssetClass.set(assetClassKey, {
            id:
                asset.asset_class?.id ??
                asset.asset_class_id ??
                nextSeriesIndex,
            name: assetClassName,
            color:
                asset.asset_class?.color_hex ??
                FALLBACK_ASSET_CLASS_CHART_COLORS[
                    nextSeriesIndex % FALLBACK_ASSET_CLASS_CHART_COLORS.length
                ],
            counts: new Map([[registrationGroup.timestamp, 1]]),
        });
    });

    return Array.from(seriesByAssetClass.values())
        .map((series) => ({
            ...series,
            total: Array.from(series.counts.values()).reduce(
                (sum, count) => sum + count,
                0,
            ),
        }))
        .sort((a, b) => {
            if (b.total !== a.total) {
                return b.total - a.total;
            }
            return a.name.localeCompare(b.name);
        })
        .map((series) => ({
            enabled: true,
            chartType: ChartTypes.column,
            item: {
                measure: {
                    id: 1,
                    slug: "issue_count",
                    name: "Issue Count",
                    unit: TimeSeriesUnits.address,
                },
                group: {
                    id: series.id,
                    type: "asset_class",
                    name: series.name,
                    slug: series.name,
                    color: series.color,
                },
                points: Array.from(series.counts.entries())
                    .sort((a, b) => a[0] - b[0])
                    .map(([timestamp, count]) => [
                        new Date(timestamp).toISOString(),
                        count,
                    ]),
            },
        }));
}

/**
 * getMultiSelectOptions
 * Builds the MultiSelect options for a filter from its FilterMetadata.selectOptions
 */
function getMultiSelectOptions(
    filterMetadata: FilterMetadata,
): MultiSelectOption[] {
    return (filterMetadata.selectOptions ?? []).map((option) => ({
        value: String(option.value),
        label: option.content.label,
        icon:
            filterMetadata.field === AssetColumns.assetClassID
                ? getAssetClassLinkMetadata(Number(option.value)).icon
                : option.content.icon,
    }));
}

/**
 * getMultiSelectValue
 * Maps a filter's value onto the MultiSelect's value (MultiSelect option values are always strings)
 */
function getMultiSelectValue(value: unknown): string[] {
    return Array.isArray(value) ? value.map(String) : [];
}

/**
 * getSelectedOptions
 * Maps the MultiSelect's value back onto the FilterMetadata.selectOptions that were selected.
 * Used to apply the original (un-stringified) SelectOption.value to the filter.
 */
function getSelectedOptions(
    filterMetadata: FilterMetadata,
    selected: string[],
): SelectOption[] {
    return (filterMetadata.selectOptions ?? []).filter((option) =>
        selected.includes(String(option.value)),
    );
}

function RegistrationGroupRow(props: {
    label: string;
    itemCount: number;
    collapsed: boolean;
    onToggle: () => void;
}) {
    const { label, collapsed, onToggle } = props;

    return (
        <tr className="bg-transparent select-none">
            <td colSpan={TABLE_COLUMNS.length} className="p-0">
                <button
                    type="button"
                    aria-expanded={!collapsed}
                    onClick={onToggle}
                    style={{
                        minWidth: `${TABLE_MIN_WIDTH}px`,
                        backgroundImage: `linear-gradient(to bottom, ${RWA_COLORS.slate[200]} 0%, ${RWA_COLORS.slate[200]} 32%, rgba(218, 226, 240, 0.9) 78%, rgba(218, 226, 240, 0.9) 100%)`,
                    }}
                    className="flex w-full cursor-pointer items-center gap-3 px-3 py-1.5 text-left text-sm font-medium text-slate-700 transition-[filter] hover:brightness-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                >
                    <span className="flex min-w-0 items-center gap-1.5">
                        {collapsed ? (
                            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                        ) : (
                            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                        )}
                        <span className="truncate">{label}</span>
                        {/* NOTE - this had to be commented out since we moved from SSG to runtime API filtering */}
                        {/* <span
                            aria-hidden="true"
                            className="h-1 w-1 shrink-0 rounded-full bg-slate-400"
                        />
                        <span className="shrink-0 text-xs font-normal text-slate-500">
                            {itemCount.toLocaleString()}{" "}
                            {itemCount === 1 ? "asset" : "assets"}
                        </span> */}
                    </span>
                </button>
            </td>
        </tr>
    );
}

// // // //

/**
 * NewAssetMonitorTable
 * Renders the filter controls + table for a single page of assets.
 * Filtering, sorting and pagination are all applied by the API (via `query` /
 * `onChangeQuery`) - the filter controls are wired up to `query.filter` with
 * FilterFormContextProvider + FilterFormField, using `filterMetadata`.
 */
function NewAssetMonitorTable(props: {
    assets: Asset[];
    filterMetadata: FilterMetadata[];
    paginationState: PaginationState;
    loading: boolean;
    query: ApiQuery;
    onChangeQuery: (updatedQuery: ApiQuery) => void;
}) {
    const {
        assets,
        filterMetadata,
        paginationState,
        loading,
        query,
        onChangeQuery,
    } = props;
    const { isLoggedIn } = useUser();
    const [selectedTableGrouping, setSelectedTableGrouping] =
        React.useState<IssueCountGroupingValue>(DEFAULT_TABLE_GROUPING);
    const [selectedIssueCountGrouping, setSelectedIssueCountGrouping] =
        React.useState<IssueCountGroupingValue>(DEFAULT_CHART_GROUPING);
    const [showIssueCountLegend, setShowIssueCountLegend] =
        React.useState(true);
    const [collapsedRegistrationGroups, setCollapsedRegistrationGroups] =
        React.useState<Set<string>>(new Set());
    const issueCountChartContainerRef = React.useRef<HTMLDivElement | null>(
        null,
    );
    const [issueCountChartHeight, setIssueCountChartHeight] =
        React.useState(320);

    const issueCountSeries = React.useMemo(() => {
        return buildIssueCountSeries(assets, selectedIssueCountGrouping);
    }, [assets, selectedIssueCountGrouping]);

    const issueCountGroupByTabOptions = React.useMemo(
        () =>
            ISSUE_COUNT_GROUPING_OPTIONS.filter(
                (option) =>
                    option.value !== "daily" && option.value !== "weekly",
            ).map((option) => ({
                label: option.label,
                value: option.value,
            })),
        [],
    );

    const tableGroupByOptions = React.useMemo(
        () =>
            ISSUE_COUNT_GROUPING_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
            })),
        [],
    );

    const tableRegistrationGroupCounts = React.useMemo(() => {
        const counts = new Map<string, number>();

        assets.forEach((asset) => {
            const groupKey = getRegistrationGroup(
                asset,
                selectedTableGrouping,
            ).key;

            counts.set(groupKey, (counts.get(groupKey) ?? 0) + 1);
        });

        return counts;
    }, [selectedTableGrouping, assets]);

    const paginatedRegistrationGroups = React.useMemo(() => {
        const groups: Array<{ key: string; label: string }> = [];

        assets.forEach((asset) => {
            const group = getRegistrationGroup(asset, selectedTableGrouping);
            const previousGroup = groups[groups.length - 1];

            if (previousGroup?.key === group.key) {
                return;
            }

            groups.push(group);
        });

        return groups;
    }, [assets, selectedTableGrouping]);

    // The filters applied by the user are the BasicFilters at the top level of
    // query.filter (the default filter is a nested CompositeFilter)
    const appliedFilterCount = (query.filter?.filters ?? []).filter(
        (filter) => filter.field !== undefined,
    ).length;

    const activeFilterCount =
        appliedFilterCount +
        (selectedTableGrouping !== DEFAULT_TABLE_GROUPING ? 1 : 0);

    const handleClearFilters = React.useCallback(() => {
        setSelectedTableGrouping(DEFAULT_TABLE_GROUPING);
        setCollapsedRegistrationGroups(new Set());

        // NOTE - the default filter is re-applied when the filter is cleared
        // (see modifyApiQuery in NewAssetMonitorListingPage)
        onChangeQuery({
            ...query,
            filter: undefined,
            pagination: {
                ...query.pagination,
                page: 1,
            },
        });
    }, [onChangeQuery, query]);

    const showRegistrationGrouping =
        query.sort.field === AssetColumns._created_at &&
        query.sort.direction !== SortDirections.none;

    const hasCollapsedGroupsOnPage =
        showRegistrationGrouping &&
        paginatedRegistrationGroups.some((group) =>
            collapsedRegistrationGroups.has(group.key),
        );

    const visibleAssetRowIndexes = React.useMemo(() => {
        const visibleIndexes = new Map<number, number>();
        let visibleIndex = 0;

        assets.forEach((asset, rawRowIndex) => {
            const groupKey = getRegistrationGroup(
                asset,
                selectedTableGrouping,
            ).key;
            const isHiddenByCollapse =
                showRegistrationGrouping &&
                collapsedRegistrationGroups.has(groupKey);

            if (isHiddenByCollapse) {
                return;
            }

            visibleIndexes.set(rawRowIndex, visibleIndex);
            visibleIndex += 1;
        });

        return visibleIndexes;
    }, [
        collapsedRegistrationGroups,
        assets,
        selectedTableGrouping,
        showRegistrationGrouping,
    ]);

    const handleTableGroupingChange = React.useCallback(
        (updatedValue: IssueCountGroupingValue) => {
            Analytics.trackGroupingChange(updatedValue);
            setSelectedTableGrouping(updatedValue);
            setCollapsedRegistrationGroups(new Set());
            onChangeQuery({
                ...query,
                pagination: {
                    ...query.pagination,
                    page: 1,
                },
                sort:
                    query.sort.field === AssetColumns._created_at &&
                    query.sort.direction !== SortDirections.none
                        ? query.sort
                        : {
                              field: AssetColumns._created_at,
                              direction: SortDirections.desc,
                          },
            });
        },
        [onChangeQuery, query],
    );

    const toggleRegistrationGroup = React.useCallback((groupKey: string) => {
        setCollapsedRegistrationGroups((prev) => {
            const next = new Set(prev);

            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }

            return next;
        });
    }, []);

    // QUESTION - WTF is this code doing???
    React.useEffect(() => {
        const container = issueCountChartContainerRef.current;

        if (!container) {
            return;
        }

        const updateHeight = (nextHeight: number) => {
            if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
                return;
            }

            setIssueCountChartHeight((prev) => {
                return Math.abs(prev - nextHeight) > 2 ? nextHeight : prev;
            });
        };

        updateHeight(Math.floor(container.getBoundingClientRect().height));

        if (typeof ResizeObserver === "undefined") {
            return;
        }

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];

            updateHeight(Math.floor(entry?.contentRect.height ?? 0));
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <FilterFormContextProvider
            filterMetadata={filterMetadata}
            query={query}
            onChange={(updatedQuery) => {
                // Ensures pagination state is reset when a filter changes
                onChangeQuery({
                    ...updatedQuery,
                    pagination: {
                        ...updatedQuery.pagination,
                        page: 1,
                    },
                });
            }}
        >
            <div className="space-y-4">
                <div className={cn("space-y-3")}>
                    <div className="space-y-3">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-start">
                            <div className="flex min-w-0 w-full flex-col gap-1.5 lg:min-w-0 lg:flex-1">
                                <FilterLabel label="Search" />

                                <SelectLoginGate
                                    isLoggedIn={isLoggedIn}
                                    tooltipText="Log in to unlock search"
                                >
                                    <FilterFormField
                                        column={SEARCH_FILTER_METADATA.field}
                                        debounce={SEARCH_DEBOUNCE_TIMEOUT}
                                    >
                                        {({ value, onChange }) => (
                                            <Input
                                                value={
                                                    typeof value === "string"
                                                        ? value
                                                        : ""
                                                }
                                                onChange={(event) => {
                                                    const updatedValue =
                                                        event.target.value;
                                                    Analytics.trackFilterSearch(
                                                        updatedValue,
                                                    );
                                                    onChange(updatedValue);
                                                }}
                                                placeholder="Search"
                                                disabled={!isLoggedIn}
                                                className={cn(
                                                    "w-full",
                                                    CONTROL_INPUT_CLASS_NAME,
                                                )}
                                            />
                                        )}
                                    </FilterFormField>
                                </SelectLoginGate>
                            </div>

                            <div className="w-full space-y-1 lg:mr-3 lg:w-33 lg:max-w-33 lg:shrink-0">
                                <FilterLabel label="Group By" />
                                <SelectLoginGate
                                    isLoggedIn={isLoggedIn}
                                    tooltipText="Log in to unlock group controls"
                                >
                                    <select
                                        value={selectedTableGrouping}
                                        onChange={(event) => {
                                            handleTableGroupingChange(
                                                event.target
                                                    .value as IssueCountGroupingValue,
                                            );
                                        }}
                                        disabled={!isLoggedIn}
                                        className={
                                            CONTROL_SELECT_TRIGGER_CLASS_NAME
                                        }
                                    >
                                        {tableGroupByOptions.map((option) => (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </SelectLoginGate>
                            </div>

                            <div className="w-full space-y-1 lg:mr-3 lg:w-50 lg:max-w-50 lg:shrink-0">
                                <FilterLabel label="Asset Class" />
                                <SelectLoginGate
                                    isLoggedIn={isLoggedIn}
                                    tooltipText="Log in to filter data"
                                >
                                    <FilterFormField
                                        column={AssetColumns.assetClassID}
                                    >
                                        {({
                                            filterMetadata,
                                            value,
                                            onChange,
                                        }) => (
                                            <MultiSelect
                                                options={getMultiSelectOptions(
                                                    filterMetadata,
                                                )}
                                                value={getMultiSelectValue(
                                                    value,
                                                )}
                                                disabled={!isLoggedIn}
                                                onChange={(selected) => {
                                                    const selectedOptions =
                                                        getSelectedOptions(
                                                            filterMetadata,
                                                            selected,
                                                        );
                                                    Analytics.trackFilterAssetClass(
                                                        selected,
                                                    );
                                                    onChange(
                                                        selectedOptions.map(
                                                            (option) =>
                                                                option.value,
                                                        ),
                                                    );
                                                }}
                                                placeholder="All classes"
                                                searchEnabled={true}
                                                searchPlaceholder="Search classes..."
                                                className="w-full"
                                                popoverWidth="w-[240px]"
                                                maxBadgesShown={1}
                                            />
                                        )}
                                    </FilterFormField>
                                </SelectLoginGate>
                            </div>

                            <div className="w-full space-y-1 lg:mr-3 lg:w-42 lg:max-w-42 lg:shrink-0">
                                <FilterLabel label="Platform" />
                                <SelectLoginGate
                                    isLoggedIn={isLoggedIn}
                                    tooltipText="Log in to filter data"
                                >
                                    <FilterFormField
                                        column={AssetColumns.protocolIDs}
                                    >
                                        {({
                                            filterMetadata,
                                            value,
                                            onChange,
                                        }) => (
                                            <MultiSelect
                                                options={getMultiSelectOptions(
                                                    filterMetadata,
                                                )}
                                                value={getMultiSelectValue(
                                                    value,
                                                )}
                                                disabled={!isLoggedIn}
                                                onChange={(selected) => {
                                                    const selectedOptions =
                                                        getSelectedOptions(
                                                            filterMetadata,
                                                            selected,
                                                        );
                                                    Analytics.trackFilterPlatform(
                                                        selectedOptions.map(
                                                            (option) =>
                                                                option.content
                                                                    .label,
                                                        ),
                                                    );
                                                    onChange(
                                                        selectedOptions.map(
                                                            (option) =>
                                                                option.value,
                                                        ),
                                                    );
                                                }}
                                                placeholder="All platforms"
                                                searchEnabled={true}
                                                searchPlaceholder="Search platforms..."
                                                className="w-full"
                                                popoverWidth="w-[240px]"
                                                maxBadgesShown={1}
                                            />
                                        )}
                                    </FilterFormField>
                                </SelectLoginGate>
                            </div>

                            <div className="w-full space-y-1 lg:w-42 lg:max-w-42 lg:shrink-0">
                                <FilterLabel label="Network" />
                                <SelectLoginGate
                                    isLoggedIn={isLoggedIn}
                                    tooltipText="Log in to filter data"
                                >
                                    <FilterFormField
                                        column={AssetColumns.networkIDs}
                                    >
                                        {({
                                            filterMetadata,
                                            value,
                                            onChange,
                                        }) => (
                                            <MultiSelect
                                                options={getMultiSelectOptions(
                                                    filterMetadata,
                                                )}
                                                value={getMultiSelectValue(
                                                    value,
                                                )}
                                                disabled={!isLoggedIn}
                                                onChange={(selected) => {
                                                    const selectedOptions =
                                                        getSelectedOptions(
                                                            filterMetadata,
                                                            selected,
                                                        );
                                                    Analytics.trackFilterNetwork(
                                                        selectedOptions.map(
                                                            (option) =>
                                                                option.content
                                                                    .label,
                                                        ),
                                                    );
                                                    onChange(
                                                        selectedOptions.map(
                                                            (option) =>
                                                                option.value,
                                                        ),
                                                    );
                                                }}
                                                placeholder="All networks"
                                                searchEnabled={true}
                                                searchPlaceholder="Search networks..."
                                                className="w-full"
                                                popoverWidth="w-[240px]"
                                                maxBadgesShown={1}
                                            />
                                        )}
                                    </FilterFormField>
                                </SelectLoginGate>
                            </div>

                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={activeFilterCount === 0}
                                className="h-9 w-full shrink-0 px-2.5 lg:ml-auto lg:w-auto select-none disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-100 disabled:text-slate-500 disabled:opacity-100 disabled:hover:bg-slate-100"
                                onClick={() => {
                                    Analytics.trackFilterClearAll({
                                        active_filter_count: activeFilterCount,
                                        source: "toolbar",
                                    });
                                    handleClearFilters();
                                }}
                                aria-label="Reset all table filters and search"
                            >
                                Reset filters
                            </Button>
                        </div>
                    </div>

                    {assets.length === 0 && (
                        <div className="flex flex-col items-center justify-center rounded-lg border bg-card px-6 py-16 text-center shadow-xs">
                            <p className="text-sm font-medium text-foreground">
                                No table rows match these controls
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Try broadening your search or clearing your
                                filters.
                            </p>
                            {activeFilterCount > 0 && (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="mt-4 px-2.5"
                                    onClick={() => {
                                        Analytics.trackFilterClearAll({
                                            active_filter_count:
                                                activeFilterCount,
                                            source: "empty_state",
                                        });
                                        handleClearFilters();
                                    }}
                                >
                                    Reset table filters
                                </Button>
                            )}
                        </div>
                    )}

                    {assets.length > 0 && (
                        <DataGrid<AssetTableRowData, AssetColumns>
                            query={query}
                            onChangeQuery={onChangeQuery}
                            rows={assets as AssetTableRowData[]}
                            columns={TABLE_COLUMNS}
                            rowKey="id"
                            bordered
                            loading={loading}
                            paginationState={paginationState}
                            analyticsTableName="new-asset-monitor-table"
                            size={RowSizes.small}
                            includeDummyRows={!hasCollapsedGroupsOnPage}
                        >
                            {({ row, rowIndex, scrollLeft }) => {
                                const asset = row as Asset;
                                const currentRegistrationGroup =
                                    getRegistrationGroup(
                                        asset,
                                        selectedTableGrouping,
                                    );
                                const previousRegistrationGroup =
                                    rowIndex > 0
                                        ? getRegistrationGroup(
                                              assets[rowIndex - 1] as Asset,
                                              selectedTableGrouping,
                                          )
                                        : null;
                                const isFirstAssetInGroup =
                                    showRegistrationGrouping &&
                                    (rowIndex === 0 ||
                                        currentRegistrationGroup.key !==
                                            previousRegistrationGroup?.key);
                                const isCollapsed =
                                    showRegistrationGrouping &&
                                    collapsedRegistrationGroups.has(
                                        currentRegistrationGroup.key,
                                    );
                                const visibleRowIndex =
                                    visibleAssetRowIndexes.get(rowIndex) ??
                                    rowIndex;

                                return (
                                    <>
                                        {isFirstAssetInGroup && (
                                            <RegistrationGroupRow
                                                label={
                                                    currentRegistrationGroup.label
                                                }
                                                itemCount={
                                                    tableRegistrationGroupCounts.get(
                                                        currentRegistrationGroup.key,
                                                    ) ?? 1
                                                }
                                                collapsed={isCollapsed}
                                                onToggle={() => {
                                                    Analytics.trackIssuanceGroupToggle(
                                                        {
                                                            group_key:
                                                                currentRegistrationGroup.key,
                                                            group_label:
                                                                currentRegistrationGroup.label,
                                                            item_count:
                                                                tableRegistrationGroupCounts.get(
                                                                    currentRegistrationGroup.key,
                                                                ) ?? 1,
                                                            action: isCollapsed
                                                                ? "expand"
                                                                : "collapse",
                                                        },
                                                    );
                                                    toggleRegistrationGroup(
                                                        currentRegistrationGroup.key,
                                                    );
                                                }}
                                            />
                                        )}
                                        {!isCollapsed && (
                                            <AssetTableRow
                                                asset={{
                                                    ...asset,
                                                    tokens: sortItemsByProperty(
                                                        {
                                                            items: asset.tokens,
                                                            field: ASSET_SORT_MAP[
                                                                query.sort
                                                                    .field as AssetColumns
                                                            ],
                                                            direction:
                                                                query.sort
                                                                    .direction,
                                                            nullsLast: true,
                                                        },
                                                    ),
                                                }}
                                                size={RowSizes.small}
                                                columns={TABLE_COLUMNS}
                                                rowIndex={visibleRowIndex}
                                                scrollLeft={scrollLeft}
                                                bordered
                                                striped
                                                defaultExpandedState={false}
                                                includeAdditionalNetworksRow={
                                                    false
                                                }
                                            />
                                        )}
                                    </>
                                );
                            }}
                        </DataGrid>
                    )}
                </div>
            </div>
        </FilterFormContextProvider>
    );
}

// // // //

/**
 * isDefaultQuery
 * Returns true when `query` requests the same page of results as
 * NEW_ASSET_MONITOR_QUERY (i.e. the statically generated `initialState`)
 */
function isDefaultQuery(query: ApiQuery): boolean {
    return (
        query.sort.field === NEW_ASSET_MONITOR_QUERY.sort.field &&
        query.sort.direction === NEW_ASSET_MONITOR_QUERY.sort.direction &&
        query.pagination.page === NEW_ASSET_MONITOR_QUERY.pagination.page &&
        query.pagination.perPage ===
            NEW_ASSET_MONITOR_QUERY.pagination.perPage &&
        areFiltersIdentical({
            currentFilter: query.filter,
            latestFilter: NEW_ASSET_MONITOR_QUERY.filter,
        })
    );
}

// // // //

export function NewAssetMonitorListingPage(props: {
    initialState: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
}) {
    const { initialState, filterMetadata } = props;

    return (
        <ApiQueryStateManager
            defaultFilters={NEW_ASSET_MONITOR_QUERY}
            modifyApiQuery={(apiQuery) => {
                // Use structuredClone for deep cloning apiQuery + the default filter
                // Prevents mutation of NEW_ASSET_MONITOR_QUERY (the filter UI
                // adds / removes filters in place)
                const clonedQuery = structuredClone(apiQuery);

                // Re-apply the default filter when the filters are cleared
                if (!clonedQuery.filter) {
                    clonedQuery.filter = structuredClone(
                        NEW_ASSET_MONITOR_QUERY.filter,
                    );
                }

                return clonedQuery;
            }}
        >
            {({ appliedQuery, internalQuery, resetQuery }) => (
                <ApiQueryFetcher
                    appliedQuery={appliedQuery.query}
                    internalQuery={internalQuery.query}
                    endpoint={ApiQueryEndpoints.AssetV4}
                    initialState={initialState}
                >
                    {(state) => {
                        // The statically generated first page is re-fetched
                        // client-side on mount - don't render it as "loading"
                        const isShowingInitialState =
                            state.appliedQueryReq.data === initialState &&
                            isDefaultQuery(appliedQuery.query);

                        return (
                            <NewAssetMonitorTable
                                assets={state.appliedQueryReq.data.results}
                                filterMetadata={filterMetadata}
                                paginationState={
                                    state.appliedQueryReq.data.pagination
                                }
                                loading={
                                    state.appliedQueryReq.loading &&
                                    !isShowingInitialState
                                }
                                query={appliedQuery.query}
                                onChangeQuery={appliedQuery.update}
                            />
                        );
                    }}
                </ApiQueryFetcher>
            )}
        </ApiQueryStateManager>
    );
}
